import { Component, HostBinding, Input, OnChanges } from '@angular/core';
import { ColorPalette, DEFAULT_TEXT_COLORS, PageSection } from '../../../models/end-client-reporting-package';
import { ChartSeries, ReportBlock, countedFigure, paletteColors, readableInk } from '../report-aggregation';

/**
 * A group that is the rest rather than a group of its own: "Other", "Other (please specify)", "None
 * of these". Drawn in the deck's inert grey, the colour it gives everything that is not a category.
 */
const LEFTOVER_GROUP = /^(other\b|none of\b)/i;

/** The deck's inert grey and the ink it carries -- see --ecr-grey. */
const LEFTOVER_FILL = 'var(--ecr-grey, #c3ced3)';
const LEFTOVER_INK = 'var(--ecr-ink, #1b2b33)';

interface MarketTile {
  market: string;
  percent: number;
  count: number;
  background: string;
  color: string;
}

@Component({
  selector: 'app-multi-market-block',
  standalone: false,
  template: `
    @if (!tiles.length) {
      <p class="grey4 nav-font12 mb-0">
        @if (!groupChosen) {
          Choose the question that says which group each respondent is in.
        } @else if (nothingCounted) {
          Tick at least one answer to count.
        } @else {
          No data for these groups.
        }
      </p>
    }

    <ul class="markets list-unstyled mb-0">
      @for (tile of tiles; track tile.market) {
        <li class="market">
          <div class="market-tile"
               [style.background-color]="tile.background"
               [style.color]="tile.color"
               [attr.title]="tile.market + ' — ' + tile.count + ' respondents'">
            <span class="market-name nav-font12">{{ tile.market }}</span>
            <span class="market-value nav-font24 font-weight-bold">
              {{ tile.percent | number: '1.0-0' }}%
            </span>
          </div>
        </li>
      }
    </ul>
  `,
  styles: [`
    :host { display: block; }

    /* Wrapping equal columns rather than a fixed grid: the number of markets is the data's to
       decide, and a survey can carry three or thirty. */
    .markets {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
    }

    .market {
      flex: 1 1 110px;
      min-width: 96px;
      text-align: center;
    }

    /* Name above figure inside the one block of colour, so a tile reads as a single object rather
       than as a label with a caption. The name is the smaller of the two: the figure is what is
       being compared across tiles, and the name only says which column you are in. */
    .market-tile {
      border-radius: 4px;
      min-height: 76px;
      padding: 10px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 2px;
    }

    /* Wraps rather than ellipsises: a market name is short, and the one that does not fit is a
       place nobody would recognise abbreviated. */
    .market-name {
      line-height: 1.2;
    }

    .market-value { line-height: 1.1; }

    :host(.is-compact) .market-tile { min-height: 62px; }
    :host(.is-compact) .market-value { font-size: 18px !important; }
  `],
})
export class MultiMarketBlockComponent implements ReportBlock, OnChanges {
  @Input({ required: true }) block!: PageSection;
  @Input({ required: true }) series!: ChartSeries[];
  @Input({ required: true }) palette!: ColorPalette;
  @Input() alignCount?: number;
  @Input() compact?: boolean;
  @Input() editing?: boolean;

  @HostBinding('class.pl-5')
  @HostBinding('class.pr-5')
  get fullWidth(): boolean {
    return this.block?.segmentLocation === 'full';
  }

  @HostBinding('class.is-compact')
  get isCompact(): boolean {
    return !!this.compact;
  }

  tiles: MarketTile[] = [];
  groupChosen = false;
  /** Every answer unticked: there is no figure to draw, rather than a fallback nobody chose. */
  nothingCounted = false;

  ngOnChanges() {
    if (this.block.reportType !== 'segment-tiles') return;

    const extra = this.block.reportExtraData;
    this.groupChosen = !!extra.groupBy;
    this.nothingCounted = !!extra.countedAnswers && !extra.countedAnswers.length;
    if (this.nothingCounted) {
      this.tiles = [];
      return;
    }

    // One series per market, each carrying the metric's whole answer list -- and a tile has room
    // for one figure, so something has to say which answers make it up.
    const markets = this.series
      .filter(entry => entry.known)
      .map(entry => {
        const figure = countedFigure(entry.points, extra.countedAnswers);
        return { market: entry.name, percent: figure.percent, count: figure.value };
      });

    // Applied to the tiles rather than to the answers inside one: for this module "sort by size"
    // and "show top" can only sensibly mean which markets lead and how many of them to draw.
    const ordered = extra.sortDescending
      ? [...markets].sort((a, b) => b.percent - a.percent)
      : markets;
    const shown = extra.topN && extra.topN > 0 ? ordered.slice(0, extra.topN) : ordered;

    // The palette's own five, round again from the first after the fifth, rather than the chart
    // ramp's lighter and darker variants of them: a tile is named on its face, so two tiles sharing
    // a colour cannot be mistaken for each other, and a sixth tile in a shade the palette does not
    // have looked like a colour from some other deck. Counted past the leftover groups, which are
    // grey, so they do not take a colour a real group could have had.
    const roles = paletteColors(this.palette);
    const dark = this.palette.darkText ?? DEFAULT_TEXT_COLORS.darkText;
    const light = this.palette.lightText ?? DEFAULT_TEXT_COLORS.lightText;
    let next = 0;
    this.tiles = shown.map(market => {
      if (LEFTOVER_GROUP.test(market.market.trim())) {
        return { ...market, background: LEFTOVER_FILL, color: LEFTOVER_INK };
      }
      const fill = roles[next++ % roles.length];
      return { ...market, background: fill, color: readableInk(fill, dark, light) };
    });
  }
}
